/* SplitView and ArtifactPane (rules 33-36; spec 6.7; split canvas after Open Canvas and LibreChat, MIT).
   The split resizes by drag, by keyboard (Alt+Left / Alt+Right, or arrows on the focused divider)
   and by presets (1/3, 1/2, 2/3), so no drag is required (WCAG 2.5.7). */
import { useRef, useState, type ReactNode } from 'react';
import { Check, Download, ExternalLink, Share2, X } from 'lucide-react';
import { Menu } from './Menu';
import { IconButton, Segmented } from './basics';
import { OptionalSlot, optionalComponent } from './optional';
import type { ShareTarget } from './Message';
import { cx } from './cx';

type ShareDialogProps = { open: boolean; onClose: () => void; resourceType: string; resourceId: string; title?: string; pinnedVersion?: string };
const ShareDialog = optionalComponent<ShareDialogProps>(import.meta.glob('../features/collab/ShareDialog.tsx'), 'ShareDialog');

const clamp = (f: number) => Math.max(0.25, Math.min(0.75, f));

export interface SplitViewProps {
  left: ReactNode;
  right: ReactNode;
  /** Fraction of the width given to the right pane. */
  fraction: number;
  onFractionChange: (f: number) => void;
  /** One pane at a time (compact/medium windows): only `right` shows. */
  stacked?: boolean;
  label?: string;
}

export function SplitView({ left, right, fraction, onFractionChange, stacked, label = 'Resize pane' }: SplitViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const step = 0.05;
  // Alt+Left / Alt+Right anywhere in the split; plain arrows, Home and End on the focused divider.
  const onAltKey = (e: React.KeyboardEvent) => {
    if (!e.altKey) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); onFractionChange(clamp(fraction + step)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); onFractionChange(clamp(fraction - step)); }
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.altKey) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); onFractionChange(clamp(fraction + step)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); onFractionChange(clamp(fraction - step)); }
    else if (e.key === 'Home') { e.preventDefault(); onFractionChange(0.75); }
    else if (e.key === 'End') { e.preventDefault(); onFractionChange(0.25); }
  };
  const onPointerDown = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const rect = el.getBoundingClientRect();
    const move = (ev: PointerEvent) => onFractionChange(clamp(1 - (ev.clientX - rect.left) / rect.width));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div
      ref={ref}
      className="split"
      data-stacked={stacked || undefined}
      style={{ ['--f' as string]: fraction }}
      onKeyDown={onAltKey}
    >
      {!stacked && <div className="split-pane">{left}</div>}
      <div
        className="splitter"
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label={`${label} (Alt+Left or Alt+Right)`}
        aria-valuemin={25}
        aria-valuemax={75}
        aria-valuenow={Math.round(fraction * 100)}
        onKeyDown={onKey}
        onPointerDown={onPointerDown}
      ><span /></div>
      <div className="split-pane">{right}</div>
    </div>
  );
}

export interface ArtifactPaneProps {
  title: string;
  icon?: ReactNode;
  badges?: ReactNode;
  versions: number;
  version: number;
  onVersionChange: (v: number) => void;
  onRestore?: (v: number) => void;
  view?: 'preview' | 'code';
  onViewChange?: (v: 'preview' | 'code') => void;
  onPreset?: (fraction: number) => void;
  onShare?: () => void;
  /** Without `onShare`, "Share" opens the collab ShareDialog for this target (pinned to the shown version). */
  share?: ShareTarget;
  onExport?: () => void;
  onNewWindow?: () => void;
  onClose?: () => void;
  status?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function ArtifactPane(p: ArtifactPaneProps) {
  const older = p.version < p.versions;
  const [sharing, setSharing] = useState(false);
  const onShare = p.onShare ?? (p.share && ShareDialog ? () => setSharing(true) : undefined);
  return (
    <section className={cx('artp', p.className)} aria-label={`Artifact: ${p.title}`} data-cap="BUILD-01">
      <div className="artp-hd">
        {p.icon}
        <b>{p.title}</b>
        {p.badges}
        <span className="sp" />
        <div className="vstrip" role="group" aria-label="Versions">
          {Array.from({ length: p.versions }, (_, i) => i + 1).map((v) => (
            <button key={v} type="button" aria-pressed={v === p.version} onClick={() => p.onVersionChange(v)}>v{v}</button>
          ))}
        </div>
        {p.onViewChange && p.view && (
          <Segmented label="View" value={p.view} onChange={p.onViewChange} options={[{ value: 'preview', label: 'Preview' }, { value: 'code', label: 'Code' }]} />
        )}
        <Menu
          label="Artifact actions"
          groups={[
            { label: 'Share', items: [{ id: 'share', label: 'Share', icon: Share2, cap: p.share && !p.onShare ? 'COLLAB-01' : undefined, onSelect: onShare }, { id: 'export', label: 'Export', icon: Download, onSelect: p.onExport }] },
            { label: 'View', items: [{ id: 'window', label: 'Open in new window', icon: ExternalLink, onSelect: p.onNewWindow }] },
            ...(p.onPreset ? [{ label: 'Pane width', chips: true, items: [['⅓', 0.33], ['½', 0.5], ['⅔', 0.67]].map(([l, f]) => ({ id: `w${l}`, label: `${l} width`, onSelect: () => p.onPreset!(f as number) })) }] : []),
          ]}
        />
        {p.onClose && <IconButton size="sm" label="Close artifact" icon={X} onClick={p.onClose} />}
      </div>
      <div className="artp-body">{p.children}</div>
      {sharing && p.share && <OptionalSlot C={ShareDialog} props={{ open: true, onClose: () => setSharing(false), ...p.share, pinnedVersion: `v${p.version}` }} />}
      <div className="artp-ft">
        <Check size={14} aria-hidden="true" /> {p.status ?? 'Saved'} · v{p.version}
        {older && p.onRestore && (
          <> · viewing an older version <button type="button" className="btn sm" onClick={() => p.onRestore!(p.version)}>Restore v{p.version} as v{p.versions + 1}</button></>
        )}
        <span className="sp" />
        <span className="hide-narrow">Drag the divider or use Alt+Left / Alt+Right</span>
      </div>
    </section>
  );
}
